<!-- Карточка груза заказа: форма одной позиции на базе cargo-модели. -->
<template>
  <div
    class="order-cargo-card"
    :class="{ 'order-cargo-card--collapsed': !isCardOpen }"
  >
    <div class="order-cargo-card__header">
      <button
        class="order-cargo-card__heading"
        type="button"
        @click="isCardOpen = !isCardOpen"
      >
        <BaseSequenceBadge
          :value="index + 1"
          :filled="isRequiredFieldsFilled"
          tooltip
          filled-tooltip-text="Обязательные поля груза заполнены"
          empty-tooltip-text="Заполните обязательные поля груза"
        />

        <span class="order-cargo-card__title-block">
          <h4 class="order-cargo-card__title">Груз</h4>
          <span class="order-cargo-card__summary">{{ summaryText }}</span>
        </span>
      </button>

      <div class="order-cargo-card__header-actions">
        <BaseDeleteIconButton
          v-if="removable"
          title="Удалить груз"
          @click="$emit('remove')"
        />
        <button
          class="order-cargo-card__toggle-icon"
          :class="{ 'is-open': isCardOpen }"
          type="button"
          :aria-label="isCardOpen ? 'Свернуть груз' : 'Раскрыть груз'"
          @click="isCardOpen = !isCardOpen"
        >
          <AppIcon name="chevronRight" :size="16" />
        </button>
      </div>
    </div>

    <template v-if="isCardOpen">
      <div class="order-cargo-card__selector">
        <BaseFormField
          class="order-cargo-card__registry-field"
          label="Позиция из реестра"
          :filled="Boolean(item.cargo_item_registry_id)"
        >
          <template #action>
            <BaseActionLink :to="{ name: 'CargoItems' }">
              Каталог грузов
            </BaseActionLink>
          </template>

          <base-select
            :model-value="item.cargo_item_registry_id"
            :options="registryOptions"
            placeholder="Выберите груз"
            :searchable="true"
            :inline-search="true"
            :with-button="true"
            :editable="Boolean(item.cargo_item_registry_id)"
            button-label="Создать новую позицию"
            edit-title="Редактировать позицию реестра"
            @update:modelValue="updateField('cargo_item_registry_id', $event)"
            @change="applyRegistryItem"
            @button-click="$emit('create-registry-item', index)"
            @edit-option="$emit('edit-registry-item', { index, id: $event })"
          />
        </BaseFormField>
      </div>

      <div class="order-cargo-card__main-grid">
        <BaseFormField
          label="Наименование"
          required
          info-key="cargo.title"
          :filled="Boolean(item.title)"
          :error="Boolean(errors[errorKey('title')])"
        >
          <base-input
            :model-value="item.title"
            type="text"
            placeholder="Название груза"
            :invalid="Boolean(errors[errorKey('title')])"
            @update:modelValue="updateField('title', $event)"
          />
        </BaseFormField>
      </div>

      <div class="order-cargo-card__details">
        <section class="order-cargo-card__subsection">
          <div class="order-cargo-card__details-title">
            Параметры веса
          </div>

          <CargoWeightFormula
            :errors="errors"
            :index="index"
            :item="item"
            @update-field="$emit('update-field', $event)"
          />
        </section>

        <section class="order-cargo-card__subsection">
          <div class="order-cargo-card__details-title">
            Параметры одного места
          </div>

          <OrderCargoDimensionsFormula
            :errors="errors"
            :index="index"
            :item="item"
            required
            @update-field="$emit('update-field', $event)"
          />
        </section>
      </div>

      <BaseInlineStatus
        v-if="shouldCreateRegistryItem"
        kind="info"
        text="При сохранении создадим позицию в каталоге грузов на основе этих данных"
      />
    </template>
  </div>
</template>

<script>
import {
  cargoNumber,
  cargoModelDimensionM,
  grossWeightKgFromCargoItem,
} from "@/utils/cargoCalculatorUtils";

import AppIcon from "@/components/Icon/AppIcon.vue";
import CargoWeightFormula from "@/components/Order/CargoWeightFormula.vue";
import OrderCargoDimensionsFormula from "@/components/Order/CargoDimensionsFormula.vue";

export default {
  name: "OrderCargoItemCard",

  components: {
    AppIcon,
    CargoWeightFormula,
    OrderCargoDimensionsFormula,
  },

  props: {
    errors: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    registryItems: {
      type: Array,
      default: () => [],
    },
    removable: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    "create-registry-item",
    "edit-registry-item",
    "remove",
    "update-field",
  ],

  data() {
    return {
      isCardOpen: true,
    };
  },

  computed: {
    registryOptions() {
      return this.registryItems.map((registryItem) => ({
        value: registryItem.id,
        label: registryItem.title,
        item: registryItem,
      }));
    },

    summaryText() {
      const title = String(this.item.title || "").trim() || "Груз не выбран";
      const facts = [
        this.item.quantity ? `${this.item.quantity} мест` : null,
        this.item.gross_weight_kg ? `${this.item.gross_weight_kg} кг` : null,
        this.item.volume_m3 ? `${this.item.volume_m3} м³` : null,
      ].filter(Boolean);

      return facts.length ? `${title} · ${facts.join(" · ")}` : title;
    },

    isRequiredFieldsFilled() {
      return Boolean(
        String(this.item.title || "").trim() &&
        cargoNumber(this.item.quantity) &&
        cargoNumber(this.item.gross_weight_kg) &&
        cargoNumber(this.item.height_m) &&
        cargoNumber(this.item.width_m) &&
        cargoNumber(this.item.length_m) &&
        cargoNumber(this.item.volume_m3)
      );
    },

    shouldCreateRegistryItem() {
      return !this.item.cargo_item_registry_id && this.isRequiredFieldsFilled;
    },
  },

  methods: {
    updateField(field, value) {
      this.$emit("update-field", { index: this.index, field, value });
    },

    applyRegistryItem(option) {
      const registryItem = option?.item;
      if (!registryItem) return;

      this.$emit("update-field", {
        index: this.index,
        values: {
          cargo_item_registry_id: registryItem.id,
          cargo_unit_id: registryItem.cargo_unit_id || null,
          title: registryItem.title || "",
          description: registryItem.description || "",
          quantity: registryItem.quantity ?? "",
          weight_kg: registryItem.weight_kg ?? "",
          gross_weight_kg:
            grossWeightKgFromCargoItem(registryItem) ?? "",
          height_m: cargoModelDimensionM(registryItem, "height"),
          width_m: cargoModelDimensionM(registryItem, "width"),
          length_m: cargoModelDimensionM(registryItem, "length"),
          volume_m3: registryItem.volume_m3 ?? "",
          auto_calculated_volume_m3: registryItem.volume_m3 ?? "",
          auto_calculated_gross_weight_kg:
            grossWeightKgFromCargoItem(registryItem) ?? "",
        },
      });
    },

    errorKey(field) {
      return `cargoItems.${this.index}.${field}`;
    },
  },
};
</script>

<style scoped>
.order-cargo-card {
  display: grid;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--color-border, #e5e7eb);
  border-radius: 8px;
  background: #fff;
}

.order-cargo-card--collapsed {
  gap: 0;
}

.order-cargo-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.order-cargo-card__heading {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.order-cargo-card__heading :deep(.base-sequence-badge) {
  margin-top: 0;
}

.order-cargo-card__title-block {
  display: grid;
  gap: 3px;
  min-width: 0;
}

.order-cargo-card__title {
  margin: 0;
  color: var(--color-dark, #111827);
  font-size: 15px;
  font-weight: 700;
}

.order-cargo-card__summary {
  overflow: hidden;
  color: var(--color-text-secondary, #6b7280);
  font-size: 12px;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-cargo-card__header-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 6px;
}

.order-cargo-card__heading:focus-visible,
.order-cargo-card__toggle-icon:focus-visible {
  outline: 2px solid var(--color-blue, #3182ce);
  outline-offset: 3px;
}

.order-cargo-card__toggle-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-secondary, #64748b);
  cursor: pointer;
  transition: transform 0.2s ease;
}

.order-cargo-card__toggle-icon.is-open {
  transform: rotate(90deg);
}

.order-cargo-card__main-grid {
  display: grid;
  gap: 12px;
  align-items: start;
  min-width: 0;
}

.order-cargo-card__selector {
  min-width: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f7;
}

.order-cargo-card__registry-field,
.order-cargo-card :deep(.custom-select),
.order-cargo-card :deep(.select-trigger),
.order-cargo-card :deep(.input-wrapper),
.order-cargo-card :deep(.input) {
  min-width: 0;
  width: 100%;
}

.order-cargo-card__details {
  display: grid;
  gap: 12px;
  min-width: 0;
}

.order-cargo-card__subsection {
  display: grid;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #eef2f7;
  min-width: 0;
}

.order-cargo-card__details-title {
  color: var(--color-text-primary, #111827);
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
}

.error-message {
  color: var(--color-danger);
  font-size: 12px;
}

</style>
